/**
 * Utilidades do encerramento da conta (LGPD, checkpoint 4): leitura do ciclo
 * devolvido por subscription/company/get.php (`closure`) para a interface.
 * As regras (carência, quem pode reverter) são do backend; aqui só formatamos.
 * Especificação: docs/features/lgpd-eliminacao/README.md
 */

// Âncora do card "Exportar meus dados" em Backup e dados (Export.js).
export const EXPORT_ANCHOR_ID = 'company-data-export';

export const CLOSURE_GRACE_DAYS = 90;

// Ciclo aberto = em carência ou com a carência vencida (aguardando eliminação manual).
export const isClosureOpen = (closure) =>
    !!closure && (closure.state === 'grace' || closure.state === 'expired');

// 'YYYY-MM-DD' ou 'YYYY-MM-DD HH:MM:SS' como data local (new Date('YYYY-MM-DD') seria UTC).
export const parseClosureDate = (value) => {
    if (!value) return null;
    const [year, month, day] = String(value).split(/[-\sT:]/).map(Number);
    if (!year || !month || !day) return null;
    const date = new Date(year, month - 1, day);
    return isNaN(date.getTime()) ? null : date;
};

const pad = (number) => String(number).padStart(2, '0');

// dd/mm/aaaa
export const formatClosureDate = (value) => {
    const date = value instanceof Date ? value : parseClosureDate(value);
    if (!date) return '';
    return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
};

// dd/mm (banner)
export const formatClosureShortDate = (value) => {
    const date = parseClosureDate(value);
    if (!date) return '';
    return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}`;
};

// Como o ciclo começou, em linguagem geral.
export const closureOriginText = (closure) => {
    const date = formatClosureDate(closure?.requested_at);
    const on = date ? ` em ${date}` : '';
    switch (closure?.origin) {
        case 'owner_request':
            return `Encerramento solicitado${on}.`;
        case 'subscription_canceled':
            return `A assinatura foi cancelada e a conta entrou em encerramento${on}.`;
        case 'trial_expired':
            return `O período de teste terminou e a conta entrou em encerramento${on}.`;
        case 'admin':
            return `O encerramento foi iniciado pela equipe da Gabriela${on}.`;
        default:
            return `A conta entrou em encerramento${on}.`;
    }
};

// "Todos os dados serão eliminados em dd/mm/aaaa (faltam N dias)."
export const closureDeadlineText = (closure) => {
    const date = formatClosureDate(closure?.scheduled_at);
    if (!date) return 'Ao fim da carência, todos os dados serão eliminados.';
    const days = Number(closure?.days_remaining);
    const remaining = Number.isFinite(days) && days > 0
        ? ` (${days === 1 ? 'falta 1 dia' : `faltam ${days} dias`})`
        : '';
    return `Todos os dados serão eliminados em ${date}${remaining}.`;
};

export const closureExpiredText = (closure) => {
    const date = formatClosureDate(closure?.scheduled_at);
    return `A carência terminou${date ? ` em ${date}` : ''}. A eliminação será feita pela equipe da Gabriela.`;
};

// Mensagem legível de um erro da API ({ error, message }); `error` pode ser só o código.
export const closureErrorMessage = (error, fallback) => {
    if (error?.message) return error.message;
    const code = error?.error;
    // Códigos (ex.: ALREADY_OPEN) e erros de rede do navegador (em inglês) viram a mensagem padrão.
    if (typeof code === 'string' && code && !/^[A-Z_]+$/.test(code) && !/fetch|network|load failed/i.test(code)) {
        return code;
    }
    return fallback;
};

export const scrollToExport = () => {
    const target = document.getElementById(EXPORT_ANCHOR_ID);
    if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
};
